import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAppSelector } from '../hooks/redux.hooks';
import chatService from '../services/chat.service';
import { ChatMessage, Conversation } from '../types/chat.types';
import { showChatNotification } from '../utils/appNotifications';

import config from '../config';
const CHAT_HUB_URL = config.CHAT_HUB_URL;

interface ReadReceipt {
  conversationId: number;
  userId: number;
  userName: string;
  readAt: string;
}

interface ChatContextType {
  conversations: Conversation[];
  activeConversationId: number | null;
  messages: Record<number, ChatMessage[]>;
  unreadTotal: number;
  isOpen: boolean;
  isChatConnected: boolean;
  readReceipts: Record<number, ReadReceipt[]>; // per conversationId
  setActiveConversation: (id: number | null) => void;
  openChat: () => void;
  closeChat: () => void;
  loadConversations: () => Promise<void>;
  loadMessages: (conversationId: number) => Promise<void>;
  sendMessage: (conversationId: number, content: string) => Promise<void>;
  sendFile: (conversationId: number, file: File, caption?: string) => Promise<void>;
  deleteMessage: (messageId: number, conversationId: number) => Promise<void>;
  startDirect: (userId: number) => Promise<void>;
  leaveGroup: (conversationId: number) => Promise<void>;
  deleteGroup: (conversationId: number) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token, user } = useAppSelector(state => state.auth);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationIdState] = useState<number | null>(null);
  const [messages, setMessages] = useState<Record<number, ChatMessage[]>>({});
  // unreadTotal is always derived from conversations — single source of truth
  const unreadTotal = conversations.reduce((sum, c) => sum + c.unreadCount, 0);
  const [isOpen, setIsOpen] = useState(false);
  const [isChatConnected, setIsChatConnected] = useState(false);
  const [readReceipts, setReadReceipts] = useState<Record<number, ReadReceipt[]>>({});

  const connectionRef     = useRef<signalR.HubConnection | null>(null);
  const activeConvIdRef   = useRef<number | null>(null);
  const isOpenRef         = useRef<boolean>(false);
  const currentUserIdRef  = useRef<number>(0);
  const pageLoadTimeRef   = useRef<number>(Date.now());

  // Keep refs in sync — refs are readable in stale callbacks without re-registration
  useEffect(() => { activeConvIdRef.current  = activeConversationId; }, [activeConversationId]);
  useEffect(() => { isOpenRef.current        = isOpen; },             [isOpen]);
  useEffect(() => { currentUserIdRef.current = user?.userId ?? 0; },  [user?.userId]);

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const convs = await chatService.getConversations();
      setConversations(convs);
      // unreadTotal is derived from conversations — no need to set manually
      // Re-join all conversation groups — ensures new convs added while offline are covered
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        for (const conv of convs) {
          connectionRef.current.invoke('JoinConversation', conv.conversationId).catch(() => {});
        }
      }
    } catch { }
  }, [isAuthenticated]);

  const loadMessages = useCallback(async (conversationId: number) => {
    try {
      const page = await chatService.getMessages(conversationId);
      setMessages(prev => ({ ...prev, [conversationId]: page.messages }));
      try { await chatService.markAsRead(conversationId); } catch {}
      setConversations(prev => prev.map(c =>
        c.conversationId === conversationId ? { ...c, unreadCount: 0 } : c
      ));
    } catch { }
  }, []);

  const setActiveConversation = useCallback(async (id: number | null) => {
    setActiveConversationIdState(id);
    if (id !== null) {
      await loadMessages(id);
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke('JoinConversation', id);
      }
    }
  }, [loadMessages]);

  const openChat  = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const processedMsgIds = useRef(new Set<number>());

  const handleIncomingMessage = useCallback((msg: ChatMessage) => {
    // Deduplicate — prevent counting the same message twice
    if (processedMsgIds.current.has(msg.messageId)) return;
    processedMsgIds.current.add(msg.messageId);
    // Keep set size manageable
    if (processedMsgIds.current.size > 500) {
      const arr = Array.from(processedMsgIds.current);
      processedMsgIds.current = new Set(arr.slice(-300));
    }

    setMessages(prev => {
      const existing = prev[msg.conversationId] ?? [];
      if (existing.some(m => m.messageId === msg.messageId)) return prev;
      return { ...prev, [msg.conversationId]: [...existing, msg] };
    });

    const isFromMe    = msg.senderId === currentUserIdRef.current;
    const isActiveConv = activeConvIdRef.current === msg.conversationId;

    setConversations(prev => prev.map(c => {
      if (c.conversationId !== msg.conversationId) return c;
      // Only count as unread if message is truly new (after page load)
      const isNew = new Date(msg.sentAt).getTime() > (pageLoadTimeRef.current - 5000);
      return {
        ...c,
        lastMessage: msg,
        unreadCount: (isActiveConv || isFromMe || !isNew) ? c.unreadCount : c.unreadCount + 1,
      };
    }));

    if (!isFromMe) {
      if (isActiveConv && isOpenRef.current) {
        chatService.markAsRead(msg.conversationId).catch(() => {});
      }

      const chatVisible = isOpenRef.current && isActiveConv;
      if (!chatVisible) {
        const preview = msg.attachmentPath && !msg.content
          ? '📎 Attachment'
          : (msg.content?.slice(0, 60) ?? '');
        showChatNotification(msg.senderName, preview);
      }
    }
  }, []);

  const handleMessageDeleted = useCallback(({ messageId, conversationId }: { messageId: number; conversationId: number }) => {
    setMessages(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] ?? []).map(m =>
        m.messageId === messageId ? { ...m, isDeleted: true, content: '' } : m
      ),
    }));
  }, []);

  const handleConversationCreated = useCallback((conv: Conversation) => {
    setConversations(prev => {
      if (prev.some(c => c.conversationId === conv.conversationId)) return prev;
      return [conv, ...prev];
    });
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('JoinConversation', conv.conversationId).catch(() => {});
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !token) {
      connectionRef.current?.stop();
      connectionRef.current = null;
      setIsChatConnected(false);
      return;
    }

    const startConnection = async () => {
      const prev = connectionRef.current;
      connectionRef.current = null;
      if (prev) {
        try { await prev.stop(); } catch { /* already stopped */ }
      }
      if (cancelled) return;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(CHAT_HUB_URL, {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on('ReceiveChatMessage', handleIncomingMessage);
      connection.on('MessageDeleted',     handleMessageDeleted);
      connection.on('ConversationCreated', handleConversationCreated);
      connection.on('AddedToGroup', () => loadConversations());
      connection.on('GroupDeleted', ({ conversationId }: { conversationId: number }) => {
        setConversations(prev => prev.filter(c => c.conversationId !== conversationId));
        if (activeConvIdRef.current === conversationId) setActiveConversationIdState(null);
      });
      connection.on('MemberLeft', () => loadConversations());
      connection.on('MessagesRead', (data: ReadReceipt) => {
        if (data.userId !== user?.userId) {
          setReadReceipts(prev => ({
            ...prev,
            [data.conversationId]: [
              ...(prev[data.conversationId] || []).filter(r => r.userId !== data.userId),
              data
            ]
          }));
        }
      });

      connection.onclose(     () => { if (!cancelled) setIsChatConnected(false); });
      connection.onreconnecting(() => { if (!cancelled) setIsChatConnected(false); });
      connection.onreconnected( () => { if (!cancelled) setIsChatConnected(true); });

      try {
        await connection.start();
        if (cancelled) { connection.stop(); return; }
        connectionRef.current = connection;
        setIsChatConnected(true);
        // Join all conversation groups after connect
        const convIds = await chatService.getConversations()
          .then(c => c.map(x => x.conversationId))
          .catch(() => [] as number[]);
        for (const id of convIds) {
          connection.invoke('JoinConversation', id).catch(() => {});
        }
        loadConversations();
      } catch {
        if (!cancelled) setIsChatConnected(false);
      }
    };

    startConnection();

    return () => {
      cancelled = true;
      connectionRef.current?.stop();
      connectionRef.current = null;
    };
  }, [isAuthenticated, token]);

  const sendMessage = useCallback(async (conversationId: number, content: string) => {
    const msg = await chatService.sendMessage(conversationId, content);
    // Show sender's message immediately; SignalR echo will be deduplicated
    handleIncomingMessage(msg);
  }, [handleIncomingMessage]);

  const sendFile = useCallback(async (conversationId: number, file: File, caption?: string) => {
    const msg = await chatService.sendFile(conversationId, file, caption);
    // Show sender's file immediately; SignalR echo will be deduplicated
    handleIncomingMessage(msg);
  }, [handleIncomingMessage]);

  const deleteMessage = useCallback(async (messageId: number, conversationId: number) => {
    await chatService.deleteMessage(messageId, conversationId);
  }, []);

  const startDirect = useCallback(async (userId: number) => {
    const conv = await chatService.getOrCreateDirect(userId);
    setConversations(prev => {
      if (prev.some(c => c.conversationId === conv.conversationId)) return prev;
      return [conv, ...prev];
    });
    setActiveConversationIdState(conv.conversationId);
    setIsOpen(true);
    await loadMessages(conv.conversationId);
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('JoinConversation', conv.conversationId).catch(() => {});
    }
  }, [loadMessages]);

  const leaveGroup = useCallback(async (conversationId: number) => {
    await chatService.leaveGroup(conversationId);
    setConversations(prev => prev.filter(c => c.conversationId !== conversationId));
    if (activeConvIdRef.current === conversationId) setActiveConversationIdState(null);
  }, []);

  const deleteGroup = useCallback(async (conversationId: number) => {
    await chatService.deleteGroup(conversationId);
    setConversations(prev => prev.filter(c => c.conversationId !== conversationId));
    if (activeConvIdRef.current === conversationId) setActiveConversationIdState(null);
  }, []);

  return (
    <ChatContext.Provider value={{
      conversations, activeConversationId, messages, unreadTotal, readReceipts,
      isOpen, isChatConnected,
      setActiveConversation, openChat, closeChat,
      loadConversations, loadMessages,
      sendMessage, sendFile, deleteMessage, startDirect,
      leaveGroup, deleteGroup,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};
